import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import Spinner from "@/components/ui/Spinner";
import { exportToCsv } from "@/charts/tableUtils";
import "@/charts/TableChart.css";

const NUM_ROWS = 10;

const formatErrorMessage = (err, t) => {
  if (!err) return "";
  const msg =
    typeof err === "object" && err !== null
      ? err.message || String(err)
      : String(err);
  if (/Failed to fetch|NetworkError|Network request failed/i.test(msg)) {
    return t(
      "common.networkError",
      "Failed to fetch data from the server. Please check your network connection.",
    );
  }
  return msg;
};

const DataTable = ({
  columns,
  data,
  title,
  idKey,
  onRowClick,
  isLoaded,
  error,
  emptyStateMessage,
  config = {
    cardPadding: "",
    cardHeight: "300px",
    cardBoxShadow: "0 6px 18px rgba(0,0,0,0.06)",
  },
  pagination = false,
  isFrozen = false,
  removeSelection,
  exportFilename,
}) => {
  const { t } = useTranslation();
  const resolvedEmptyState =
    emptyStateMessage || t("common.noData", "No data available to display.");
  // Ensure any provided config merges with defaults so partial configs don't omit keys
  const mergedConfig = {
    cardPadding: "",
    cardHeight: "300px",
    cardBoxShadow: "0 6px 18px rgba(0,0,0,0.06)",
    ...config,
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const totalPages = pagination
    ? Math.max(Math.ceil(data.length / NUM_ROWS), 1)
    : 1;
  const startIdx = (currentPage - 1) * NUM_ROWS;
  const endIdx = startIdx + NUM_ROWS;
  // Use all data when pagination is false, otherwise use paginated data
  const displayData = pagination ? data.slice(startIdx, endIdx) : data;

  const handlePrev = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  // Reset to the first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  if (!isLoaded || error) {
    return (
      <div
        className={`card card-radius shadow-sm ${mergedConfig.cardPadding} d-flex align-items-center justify-content-center`}
        style={{
          overflow: "hidden",
          borderColor: "var(--border)",
          height: mergedConfig.cardHeight,
          boxShadow: mergedConfig.cardBoxShadow,
        }}
      >
        {error ? (
          <div
            className="d-flex align-items-center justify-content-center"
            role="alert"
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "color-mix(in srgb, var(--error) 8%, #fff)",
              border: "1px solid color-mix(in srgb, var(--error) 25%, #fff)",
              borderRadius: "8px",
              padding: "1rem",
            }}
          >
            <div style={{ color: "var(--error-dark)" }}>
              {t("common.errorOccurred", "Sorry, an error occurred: {{error}}.", {
                error: formatErrorMessage(error, t),
              })}
            </div>
          </div>
        ) : (
          <Spinner data-testid="data-table-spinner" />
        )}
      </div>
    );
  }

  return (
    <div
      className="card card-radius"
      style={{
        overflow: "hidden",
        boxShadow: mergedConfig.cardBoxShadow,
        borderColor: "var(--border)",
        ...(!pagination && mergedConfig.cardHeight
          ? { minHeight: mergedConfig.cardHeight }
          : {}),
        ...(isFrozen ? { pointerEvents: "none", opacity: 0.6 } : {}),
      }}
    >
      {(title || exportFilename) && (
        <div
          className="card-header d-flex justify-content-between align-items-center"
          style={{
            borderBottomColor: "var(--border)",
            color: "var(--text-primary)",
          }}
        >
          {title && <div className="h6 mb-0">{title}</div>}
          {exportFilename && (
            <button
              onClick={() => exportToCsv(data, columns, exportFilename)}
              className="table-export-btn ms-auto"
              title={t("common.exportCsv", "Export to CSV")}
            >
              <Download size={15} />
            </button>
          )}
        </div>
      )}
      <div
        className="table-responsive"
        style={{
          // Add scroll styling when pagination is false
          ...(pagination
            ? {}
            : {
                maxHeight: mergedConfig.cardHeight || "300px",
                overflowY: "auto",
              }),
        }}
      >
        <style>
          {`
            .custom-hover-row:hover:not(.custom-selected-row) {
              background-color: var(--color-bg-hover-1) !important;
            }
            .custom-hover-row:hover:not(.custom-selected-row) td {
              background-color: var(--color-bg-hover-1) !important;
            }
            .custom-selected-row {
              background-color: var(--color-bg-hover-1) !important;
            }
            .custom-selected-row td {
              background-color: var(--color-bg-hover-1) !important;
            }
            .dt-page-btn {
              border: 1px solid var(--border);
              background: var(--bg-elevated);
              color: var(--text-secondary);
              border-radius: 6px;
              transition: background 0.15s, border-color 0.15s, color 0.15s;
            }
            .dt-page-btn:hover:not(:disabled) {
              background: var(--color-bg-hover-1);
              border-color: var(--brand-color-1);
              color: var(--brand-color-1);
            }
            .dt-page-btn:disabled {
              opacity: 0.45;
              cursor: not-allowed;
            }
          `}
        </style>
        {displayData.length > 0 ? (
          <table className="table mb-0" data-testid="data-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      borderBottomColor: "var(--border)",
                      backgroundColor: "var(--bg-table-header)",
                      ...(column.width ? { width: column.width } : {}),
                    }}
                    className="px-3 py-2 border-bottom text-sm fw-bold align-middle"
                    data-testid={
                      column.key ? `header-${column.key}` : undefined
                    }
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((item, index) => (
                <tr
                  key={index}
                  className={
                    "custom-hover-row " +
                    (selectedRowId === item[idKey] && !removeSelection
                      ? "custom-selected-row "
                      : "") +
                    (onRowClick && !isFrozen ? "cursor-pointer" : "")
                  }
                  style={{
                    ...(onRowClick && !isFrozen ? { cursor: "pointer" } : {}),
                    transition: "background-color 0.15s ease-in-out",
                  }}
                  onClick={() => {
                    if (!isFrozen) {
                      setSelectedRowId(item[idKey]);
                      if (onRowClick) onRowClick(item);
                    }
                  }}
                  data-testid={`data-table-row-${item[idKey]}`}
                >
                  {columns.map((column) => (
                    <td key={column.key} className="align-middle">
                      {column.render
                        ? column.render(
                            item[column.accessor || column.key],
                            item,
                          )
                        : item[column.accessor || column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            role="alert"
            style={{
              margin: "1rem",
              padding: "0.75rem 1rem",
              backgroundColor: "color-mix(in srgb, var(--warning) 10%, #fff)",
              border: "1px solid color-mix(in srgb, var(--warning) 30%, #fff)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "0.95rem",
            }}
          >
            {resolvedEmptyState}
          </div>
        )}
      </div>
      {/* Pagination controls - only show when pagination is true */}
      {pagination && (
        <div
          className="px-3 py-2 border-top"
          style={{ backgroundColor: "var(--bg-surface)" }}
        >
          <div className="d-flex justify-content-center align-items-center">
            <button
              className="btn btn-sm dt-page-btn me-3"
              onClick={handlePrev}
              disabled={currentPage === 1 || isFrozen}
              style={{ minWidth: "30px" }}
            >
              &lt;
            </button>

            <span className="text-sm text-muted">
              {t("common.pageOf", {
                current: currentPage,
                total: totalPages,
                defaultValue: `Page ${currentPage} of ${totalPages}`,
              })}
            </span>

            <button
              className="btn btn-sm dt-page-btn ms-3"
              onClick={handleNext}
              disabled={currentPage === totalPages || isFrozen}
              style={{ minWidth: "30px" }}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
